import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProduct, isEnquiryCategory, submitEnquiry, getProductImages, isRealEstateCategory } from '../marketApi';
import { useCart } from '../CartContext';
import { formatPrice } from '../currency';

const COLORS = [
  '#2563eb', '#7c3aed', '#db2777', '#d97706',
  '#16a34a', '#0891b2', '#dc2626', '#65a30d',
];

function colorFor(str) {
  if (!str) return COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
  return COLORS[h % COLORS.length];
}

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function StockLabel({ stock }) {
  if (stock === undefined || stock === null) return null;
  if (stock === 0) return <span className="stock-out">Out of Stock</span>;
  if (stock <= 10) return <span className="stock-low">Low Stock — only {stock} left</span>;
  return <span className="stock-in">In Stock</span>;
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [enquirySent, setEnquirySent] = useState(false);
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProduct(id)
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Product not found');
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const mainUrl = product.image_url || product.imageUrl || '';
    const mainItem = mainUrl ? [{ url: mainUrl, mediaType: 'image' }] : [];
    getProductImages(product.id)
      .then(imgs => {
        const extra = imgs
          .filter(i => i.image_url)
          .map(i => ({ url: i.image_url, mediaType: i.media_type || 'image' }));
        const dedupExtra = extra.filter(e => e.url !== mainUrl);
        const ytId = extractYouTubeId(product.youtube_url || product.youtubeUrl || '');
        const ytItem = ytId ? [{
          url: `https://www.youtube.com/embed/${ytId}?rel=0`,
          mediaType: 'youtube',
          thumbUrl: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`,
        }] : [];
        const all = [...mainItem, ...dedupExtra, ...ytItem];
        setPhotos(all.length > 0 ? all : mainItem);
      })
      .catch(() => { setPhotos(mainItem); });
  }, [product]);

  function cartQty() {
    const item = cart.find((i) => i.productId === Number(id) || i.productId === id);
    return item ? item.quantity : 0;
  }

  function handleAdd() {
    addToCart(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight' && photos.length > 1) setPhotoIdx(i => (i + 1) % photos.length);
      if (e.key === 'ArrowLeft' && photos.length > 1) setPhotoIdx(i => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, photos.length, closeLightbox]);

  function discountPct() {
    const orig = product.original_price || product.originalPrice;
    if (!orig || orig <= product.price) return 0;
    return Math.round(((orig - product.price) / orig) * 100);
  }

  if (loading) {
    return (
      <div className="page-container">
        <div
          className="mk-detail-skel"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
            marginTop: 8,
          }}
        >
          <div className="skeleton" style={{ aspectRatio: '1 / 1', width: '100%', borderRadius: 12 }} />
          <div>
            <div className="skeleton" style={{ height: 28, width: '80%', marginBottom: 12 }} />
            <div className="skeleton" style={{ height: 16, width: '50%', marginBottom: 20 }} />
            <div className="skeleton" style={{ height: 36, width: 120, marginBottom: 20 }} />
            <div className="skeleton" style={{ height: 14, width: '90%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 14, width: '75%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 14, width: '60%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: 60 }}>
        <div style={{ fontSize: 48 }}>😕</div>
        <h2 style={{ marginTop: 12 }}>Product not found</h2>
        <p style={{ color: 'var(--muted)', marginTop: 8 }}>{error}</p>
        <Link to="/" className="btn btn-primary" style={{ marginTop: 20, display: 'inline-flex' }}>
          Back to Home
        </Link>
      </div>
    );
  }

  const isOutOfStock = product.stock === 0;
  const dp = discountPct();
  const sellerName = product.seller_name || product.sellerName || product.seller?.businessName || '';
  const sellerPhone = product.seller_phone || product.sellerPhone || product.seller?.phone || '';
  const city = product.city || product.seller?.city || '';
  const catName = product.category_name || product.categoryName || product.category?.name || '';
  const imageUrl = product.image_url || product.imageUrl || '';
  const isEnquiry = isEnquiryCategory(catName);
  const isRealEstate = isRealEstateCategory(catName);

  async function handleEnquirySubmit(e) {
    e.preventDefault();
    if (!enquiryForm.name.trim()) { setEnquiryError('Please enter your name'); return; }
    if (!/^\d{10}$/.test(enquiryForm.phone.trim())) { setEnquiryError('Please enter a valid 10-digit phone number'); return; }
    if (!enquiryForm.message.trim()) { setEnquiryError('Please describe your requirement'); return; }
    setEnquiryError('');
    setEnquirySubmitting(true);
    try {
      await submitEnquiry({
        productId: product.id,
        productName: product.name,
        sellerId: product.seller_id || product.sellerId || product.seller?.id,
        name: enquiryForm.name.trim(),
        phone: enquiryForm.phone.trim(),
        email: enquiryForm.email.trim(),
        message: enquiryForm.message.trim(),
      });
      setEnquirySent(true);
    } catch (err) {
      setEnquiryError(err.message || 'Failed to send enquiry. Please try again.');
    } finally {
      setEnquirySubmitting(false);
    }
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: 16 }}>
        <Link to="/" style={{ color: 'var(--muted)', fontSize: 13 }}>
          ← Back to products
        </Link>
      </div>

      <div
        className="mk-detail-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px,1fr) minmax(280px,1fr)',
          gap: 28,
          alignItems: 'start',
        }}
      >
        {/* Image / Photo + Video Carousel */}
        <div style={{ width: '100%', background: '#000', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          {photos.length > 0 ? (
            <div style={{ position: 'relative' }}>
              {/* Main media */}
              <div
                style={{ width: '100%', aspectRatio: '4/3', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: photos[photoIdx]?.mediaType === 'image' ? 'zoom-in' : 'default' }}
                onClick={() => { if (photos[photoIdx]?.mediaType === 'image') setLightboxOpen(true); }}
              >
                {photos[photoIdx]?.mediaType === 'video' ? (
                  <video
                    key={photos[photoIdx].url}
                    src={photos[photoIdx].url}
                    controls
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                ) : photos[photoIdx]?.mediaType === 'pdf' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
                    <span style={{ fontSize: 64 }}>📄</span>
                    <span style={{ color: '#fff', fontSize: 14, textAlign: 'center' }}>
                      {decodeURIComponent(photos[photoIdx].url.split('/').pop().split('?')[0]).slice(0, 40) || 'Document'}
                    </span>
                    <a
                      href={photos[photoIdx].url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="btn btn-outline"
                      style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontSize: 14 }}
                    >
                      📥 Open / Download PDF
                    </a>
                  </div>
                ) : photos[photoIdx]?.mediaType === 'youtube' ? (
                  <iframe
                    key={photos[photoIdx].url}
                    src={photos[photoIdx].url}
                    style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="YouTube video"
                  />
                ) : (
                  <img
                    src={photos[photoIdx]?.url}
                    alt={product.name}
                    onError={() => setImgError(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff', display: 'block' }}
                  />
                )}
              </div>

              {/* Enlarge button — images only */}
              {photos[photoIdx]?.mediaType === 'image' && (
                <button
                  onClick={() => setLightboxOpen(true)}
                  title="View full size"
                  style={{
                    position: 'absolute', top: 8, right: 8, zIndex: 3,
                    background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none',
                    borderRadius: 6, width: 34, height: 34, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                  }}
                >
                  ⛶
                </button>
              )}

              {/* Arrows */}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)}
                    style={{
                      position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%',
                      width: 38, height: 38, fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >‹</button>
                  <button
                    onClick={() => setPhotoIdx(i => (i + 1) % photos.length)}
                    style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%',
                      width: 38, height: 38, fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >›</button>
                  {/* Counter + type badge */}
                  <div style={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 6, alignItems: 'center', zIndex: 2 }}>
                    {photos[photoIdx]?.mediaType === 'video' && (
                      <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '2px 7px', borderRadius: 8 }}>🎥 VIDEO</span>
                    )}
                    {photos[photoIdx]?.mediaType === 'pdf' && (
                      <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '2px 7px', borderRadius: 8 }}>📄 PDF</span>
                    )}
                    {photos[photoIdx]?.mediaType === 'youtube' && (
                      <span style={{ background: 'rgba(255,0,0,0.85)', color: '#fff', fontSize: 11, padding: '2px 7px', borderRadius: 8 }}>▶ YOUTUBE</span>
                    )}
                    <span style={{ background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 12, padding: '2px 8px', borderRadius: 10 }}>
                      {photoIdx + 1} / {photos.length}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Placeholder letter */
            <div style={{ width: '100%', aspectRatio: '4/3', background: colorFor(product.name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, color: '#fff', fontWeight: 700 }}>
              {(product.name || 'P').charAt(0).toUpperCase()}
            </div>
          )}

          {/* Thumbnail strip */}
          {photos.length > 1 && (
            <div style={{ display: 'flex', gap: 6, padding: 8, overflowX: 'auto', background: '#111' }}>
              {photos.map((item, i) => (
                <button
                  key={i}
                  onClick={() => setPhotoIdx(i)}
                  style={{
                    flexShrink: 0, width: 60, height: 60, position: 'relative',
                    border: i === photoIdx ? '2px solid #60a5fa' : '2px solid transparent',
                    borderRadius: 6, overflow: 'hidden', padding: 0, cursor: 'pointer', background: '#000',
                  }}
                >
                  {item.mediaType === 'video' ? (
                    <>
                      <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                        <span style={{ color: '#fff', fontSize: 16 }}>▶</span>
                      </div>
                    </>
                  ) : item.mediaType === 'pdf' ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e' }}>
                      <span style={{ fontSize: 22 }}>📄</span>
                    </div>
                  ) : item.mediaType === 'youtube' ? (
                    <>
                      <img src={item.thumbUrl} alt="YouTube" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                        <span style={{ color: '#ff0000', fontSize: 20, filter: 'drop-shadow(0 0 4px #fff)' }}>▶</span>
                      </div>
                    </>
                  ) : (
                    <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          {/* Category */}
          {catName && (
            <span className="badge badge-primary" style={{ marginBottom: 8 }}>
              {catName}
            </span>
          )}

          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8, lineHeight: 1.2 }}>
            {product.name}
          </h1>

          {/* Seller */}
          {sellerName && (
            isRealEstate ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{sellerName}</span>
                {city && <span className="badge badge-muted">{city}</span>}
                {sellerPhone && (
                  <a
                    href={`tel:${sellerPhone}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}
                  >
                    📞 {sellerPhone}
                  </a>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ color: 'var(--muted)', fontSize: 14 }}>Sold by</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{sellerName}</span>
                {city && <span className="badge badge-muted">{city}</span>}
              </div>
            )
          )}

          {/* Price row */}
          {isRealEstate ? (
            <div style={{ marginBottom: 16 }}>
              {(product.original_price || product.originalPrice) > product.price ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                  <span className="price price-lg">{formatPrice(product.price, product.currency)}</span>
                  <span style={{ fontSize: 18, color: 'var(--muted)', fontWeight: 500 }}>–</span>
                  <span className="price price-lg">{formatPrice(product.original_price || product.originalPrice, product.currency)}</span>
                  <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 4 }}>Price Range</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="price price-lg">{formatPrice(product.price, product.currency)}</span>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>onwards</span>
                </div>
              )}
              {product.unit && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>per {product.unit}</div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span className="price price-lg">
                {formatPrice(product.price, product.currency)}
                <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--muted)' }}>
                  /{product.unit || 'piece'}
                </span>
              </span>
              {(product.original_price || product.originalPrice) > product.price && (
                <span className="original-price" style={{ fontSize: 16 }}>
                  {formatPrice(product.original_price || product.originalPrice, product.currency)}
                </span>
              )}
              {dp > 0 && <span className="discount-badge">{dp}% OFF</span>}
            </div>
          )}

          {/* Stock */}
          {!isRealEstate && (
            <div style={{ marginBottom: 16 }}>
              <StockLabel stock={product.stock} />
            </div>
          )}

          {/* Description */}
          {product.description && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Description</div>
              <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6, textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                {product.description}
              </p>
            </div>
          )}

          {/* Specifications */}
          {(() => {
            let specs = [];
            try {
              const raw = product.specifications;
              if (raw) specs = typeof raw === 'string' ? JSON.parse(raw) : raw;
            } catch (_) {}
            if (!Array.isArray(specs) || specs.length === 0) return null;
            return (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Specifications</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {specs.filter(s => s.key || s.value).map((s, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'transparent' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text)', width: '40%', borderBottom: '1px solid var(--border)' }}>{s.key}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {isEnquiry ? (
            /* ── Enquiry form for Services / Real Estate ── */
            enquirySent ? (
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: 'var(--radius)', padding: '20px 16px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Enquiry Sent!</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  The seller will contact you shortly on the number you provided.
                </div>
              </div>
            ) : (
              <form onSubmit={handleEnquirySubmit}>
                <div style={{
                  background: '#eff6ff', border: '1px solid #bfdbfe',
                  borderRadius: 'var(--radius-sm)', padding: '10px 14px',
                  fontSize: 13, color: '#1e40af', marginBottom: 16,
                }}>
                  📩 This is a service listing. Send your requirement directly to the seller.
                </div>
                {enquiryError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{enquiryError}</div>}
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input className="form-control" placeholder="Full name"
                    value={enquiryForm.name}
                    onChange={e => setEnquiryForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number *</label>
                  <input className="form-control" placeholder="10-digit mobile number"
                    inputMode="numeric" maxLength={10}
                    value={enquiryForm.phone}
                    onChange={e => setEnquiryForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
                  <input className="form-control" type="email" placeholder="your@email.com"
                    value={enquiryForm.email}
                    onChange={e => setEnquiryForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Your Requirement *</label>
                  <textarea className="form-control" rows={3}
                    placeholder="Describe what you need..."
                    value={enquiryForm.message}
                    onChange={e => setEnquiryForm(f => ({ ...f, message: e.target.value }))} />
                </div>
                <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={enquirySubmitting}>
                  {enquirySubmitting ? 'Sending...' : '📩 Send Enquiry'}
                </button>
              </form>
            )
          ) : (
            <>
              {/* Qty selector */}
              {!isOutOfStock && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Qty:</span>
                  <div className="qty-control">
                    <button
                      className="qty-btn"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      disabled={qty <= 1}
                    >
                      −
                    </button>
                    <span className="qty-value">{qty}</span>
                    <button
                      className="qty-btn"
                      onClick={() =>
                        setQty((q) => (product.stock ? Math.min(product.stock, q + 1) : q + 1))
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className={`btn btn-lg ${added ? 'btn-accent' : 'btn-primary'}`}
                  onClick={handleAdd}
                  disabled={isOutOfStock}
                  style={{ flex: 1, minWidth: 160 }}
                >
                  {isOutOfStock
                    ? 'Out of Stock'
                    : added
                    ? '✓ Added to Cart!'
                    : cartQty() > 0
                    ? `Add More (${cartQty()} in cart)`
                    : '🛒 Add to Cart'}
                </button>
                {cartQty() > 0 && !isOutOfStock && (
                  <button
                    className="btn btn-lg btn-outline"
                    onClick={() => navigate('/cart')}
                    style={{ flex: 1, minWidth: 140 }}
                  >
                    View Cart
                  </button>
                )}
              </div>

              {/* COD note */}
              <div
                style={{
                  marginTop: 16,
                  padding: '10px 14px',
                  background: '#f0fdf4',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                  color: 'var(--success)',
                  fontWeight: 500,
                }}
              >
                💳 Cash on Delivery available
              </div>
            </>
          )}
        </div>
      </div>

      {/* Terms & Conditions panel */}
      {product.terms_and_conditions && (
        <div style={{
          marginTop: 28,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
        }}>
          <div style={{
            background: 'var(--surface)',
            padding: '10px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: 15 }}>📋</span>
            <span style={{ fontWeight: 700, fontSize: 13 }}>Terms &amp; Conditions</span>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <p style={{
              color: 'var(--muted)',
              fontSize: 13,
              lineHeight: 1.7,
              margin: 0,
              textAlign: 'justify',
              whiteSpace: 'pre-wrap',
            }}>
              {product.terms_and_conditions}
            </p>
          </div>
        </div>
      )}

      {/* Lightbox — images only */}
      {lightboxOpen && photos[photoIdx]?.mediaType === 'image' && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'fixed', top: 16, right: 20, zIndex: 10000,
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 42, height: 42, fontSize: 22,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>

          {/* Prev */}
          {photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => (i - 1 + photos.length) % photos.length); }}
              style={{
                position: 'fixed', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 10000,
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
                borderRadius: '50%', width: 48, height: 48, fontSize: 28,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >‹</button>
          )}

          {/* Image */}
          <img
            src={photos[photoIdx]?.url}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '92vw', maxHeight: '90vh',
              objectFit: 'contain', borderRadius: 6,
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
            }}
          />

          {/* Next */}
          {photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => (i + 1) % photos.length); }}
              style={{
                position: 'fixed', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 10000,
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
                borderRadius: '50%', width: 48, height: 48, fontSize: 28,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >›</button>
          )}

          {/* Counter */}
          {photos.length > 1 && (
            <div style={{
              position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
              background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 20,
              padding: '4px 14px', fontSize: 13,
            }}>
              {photoIdx + 1} / {photos.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
